<script setup>
import { computed } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import { useSurveyLabels } from '../composables/useSurveyLabels';

/**
 * Distribution of the numeric scores collected by the survey. Rendered with
 * plain bars (no chart dependency) so it stays legible at any width.
 */
const props = defineProps({
  distribution: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});

const { t } = useSurveyLabels();

const hasData = computed(() =>
  props.distribution.some(item => Number(item.count) > 0)
);

const maxCount = computed(() =>
  props.distribution.reduce(
    (max, item) => Math.max(max, Number(item.count) || 0),
    0
  )
);

const barWidth = item =>
  maxCount.value
    ? Math.max(4, ((Number(item.count) || 0) / maxCount.value) * 100)
    : 0;
</script>

<template>
  <LaisCard :title="t('SURVEYS.RESPONSES.DISTRIBUTION.TITLE')" density="tight">
    <div v-if="loading" class="flex flex-col gap-2" aria-hidden="true">
      <div
        v-for="index in 5"
        :key="index"
        class="h-6 w-full animate-pulse rounded-lais bg-lais-bg-soft"
      />
    </div>

    <p
      v-else-if="!hasData"
      class="rounded-lais border border-dashed border-lais-border px-3 py-6 text-center text-lais-body-sm text-lais-text-muted"
    >
      {{ t('SURVEYS.RESPONSES.DISTRIBUTION.EMPTY') }}
    </p>

    <ul v-else role="list" class="flex flex-col gap-2">
      <li
        v-for="item in distribution"
        :key="item.score"
        class="flex items-center gap-3"
      >
        <span
          class="w-8 shrink-0 text-right text-lais-body-sm font-semibold tabular-nums text-lais-text"
        >
          {{ item.score }}
        </span>
        <span
          class="h-3 min-w-0 flex-1 overflow-hidden rounded-full bg-lais-bg-soft"
        >
          <span
            class="block h-full rounded-full bg-lais-primary transition-all"
            :style="{ width: `${barWidth(item)}%` }"
          />
        </span>
        <span
          class="w-20 shrink-0 text-right text-lais-label-sm tabular-nums text-lais-text-muted"
        >
          {{ t('SURVEYS.RESPONSES.DISTRIBUTION.COUNT', { count: item.count }) }}
        </span>
      </li>
    </ul>
  </LaisCard>
</template>
